import { useAuth } from 'react-oidc-context';
import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2, Lock } from 'lucide-react';
import { isOidcConfigured } from '../../lib/auth';

interface ShipRequestRouteProps {
  children: React.ReactNode;
}

export function ShipRequestRoute({ children }: ShipRequestRouteProps) {
  if (!isOidcConfigured()) {
    return <>{children}</>;
  }
  return <ShipRequestGate>{children}</ShipRequestGate>;
}

function ShipRequestGate({ children }: { children: React.ReactNode }) {
  const auth = useAuth();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user?.profile?.email) return;

    setChecking(true);
    fetch('/api/ship-access')
      .then((r) => r.json())
      .then(({ users }: { users: { email: string; role: string }[] }) => {
        const userEmail = (auth.user?.profile?.email as string).toLowerCase();
        setAllowed(users.some((u) => u.email.toLowerCase() === userEmail));
      })
      .catch(() => setAllowed(false))
      .finally(() => setChecking(false));
  }, [auth.isAuthenticated, auth.user]);

  if (auth.isLoading || (auth.isAuthenticated && checking)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (allowed === false) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4 max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900">Access Restricted</h2>
          <p className="text-gray-500 text-sm">
            You don't have permission to submit ship requests. Contact IT or your manager to
            request access.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
